import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { PALETTE, RADIUS, press } from '../theme';

/**
 * The Duolingo button.
 *
 * Ported from the animata reference (animata.design/docs/button/duolingo), which
 * builds the effect with `border-b-4` plus `active:border-b-0 active:border-t-4`.
 * The mechanic here is identical — a 4px solid edge under the face, and pressing
 * moves the face down onto it — but it is drawn with a box-shadow instead:
 *
 *   - A 4px *border* on a 16px radius renders as a lopsided crescent at the
 *     corners, where a shadow follows the radius exactly.
 *   - Swapping a bottom border for a top border changes the content box, so the
 *     label shifts inside the button. Transform + shadow costs no layout at all.
 *
 * Everything else is kept: uppercase, bold, wide tracking, `brightness(1.1)` on
 * hover, and the face landing flush on the edge when pressed.
 */

export type DuoVariant = 'primary' | 'success' | 'secondary' | 'ghost';
export type DuoSize = 'sm' | 'md' | 'lg';

/** Face, label and edge per variant. A variant is never colour alone — the
 *  secondary keeps a visible border so it reads as a control on white. */
const VARIANTS: Record<DuoVariant, { bg: string; color: string; depth: string; border?: string; hoverBg?: string }> = {
  primary: { bg: PALETTE.signal, color: '#ffffff', depth: PALETTE.signalDepth },
  success: { bg: PALETTE.green, color: '#ffffff', depth: PALETTE.greenDepth },
  secondary: {
    bg: PALETTE.ground,
    color: PALETTE.signal,
    depth: PALETTE.controlDepth,
    border: `2px solid ${PALETTE.hairline}`,
    hoverBg: PALETTE.groundSunken,
  },
  /* Flat on purpose: a tertiary action has no face to sink, so it keeps only a
     small nudge rather than a full press. */
  ghost: { bg: 'transparent', color: PALETTE.inkMuted, depth: 'transparent' },
};

const SIZES: Record<DuoSize, { height: number; px: number; fontSize: number }> = {
  sm: { height: 40, px: 16, fontSize: 13 },
  md: { height: 48, px: 24, fontSize: 15 },
  lg: { height: 58, px: 36, fontSize: 17 },
};

export type DuoButtonProps = Omit<ButtonProps, 'variant' | 'size' | 'color'> & {
  duoVariant?: DuoVariant;
  duoSize?: DuoSize;
  block?: boolean;
  /** Set when rendering as an anchor: `component="a" href="…"`. */
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
};

export default function DuoButton({
  duoVariant = 'primary',
  duoSize = 'md',
  block = false,
  sx,
  ...props
}: DuoButtonProps) {
  const v = VARIANTS[duoVariant];
  const s = SIZES[duoSize];
  const isGhost = duoVariant === 'ghost';

  /* The shared press, then the variant's own hover colour folded into it. A white
     face needs a background change on hover, because `brightness(1.1)` on white
     is a no-op. */
  const pressed = press(v.depth);
  const motion = isGhost
    ? {
        transition: 'color 150ms ease, background-color 150ms ease, transform 150ms ease',
        '&:hover': { backgroundColor: PALETTE.groundSunken, color: PALETTE.ink },
        '&:active': { transform: 'translateY(1px)' },
      }
    : {
        ...pressed,
        '@media (hover: hover)': {
          '&:hover': {
            ...pressed['@media (hover: hover)']['&:hover'],
            ...(v.hoverBg ? { backgroundColor: v.hoverBg, borderColor: PALETTE.inkFaint } : null),
          },
        },
      };

  return (
    <Button
      disableElevation
      disableRipple
      {...props}
      sx={{
        height: s.height,
        px: `${s.px}px`,
        minWidth: 0,
        width: block ? '100%' : 'auto',
        flexShrink: 0,
        borderRadius: `${RADIUS.control}px`,
        border: v.border ?? '2px solid transparent',
        backgroundColor: v.bg,
        color: v.color,
        fontSize: s.fontSize,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        lineHeight: 1,

        ...motion,

        /* Disabled loses the edge too — a control that still looks raised invites
           a press that will not happen. */
        '&.Mui-disabled': {
          backgroundColor: isGhost ? 'transparent' : v.bg,
          color: v.color,
          opacity: 0.45,
          boxShadow: 'none',
          transform: 'none',
        },

        ...sx,
      }}
    />
  );
}
